import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { groupDashboardApi, localiApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  Loader2,
  Download,
  BarChart3,
  Wallet,
  TrendingUp,
  Building2,
  CalendarDays,
  Layers,
  PiggyBank,
  Receipt,
  FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '../lib/utils';

const PRESETS = [
  { value: 'oggi', label: 'Oggi' },
  { value: 'giorno', label: 'Singolo giorno' },
  { value: 'mese', label: 'Questo mese' },
  { value: 'anno', label: "Quest'anno" },
  { value: 'anno_scorso', label: 'Anno scorso' },
  { value: 'custom', label: 'Personalizzato' },
];

const toYmd = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fromYmd = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const computeRange = (preset, singleDay, customRange) => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = (today.getMonth() + 1).toString().padStart(2, '0');
  const dd = today.getDate().toString().padStart(2, '0');
  if (preset === 'oggi') {
    return { data_inizio: `${yyyy}-${mm}-${dd}`, data_fine: `${yyyy}-${mm}-${dd}` };
  }
  if (preset === 'giorno') {
    const d = singleDay ? toYmd(singleDay) : `${yyyy}-${mm}-${dd}`;
    return { data_inizio: d, data_fine: d };
  }
  if (preset === 'mese') {
    return { data_inizio: `${yyyy}-${mm}-01`, data_fine: `${yyyy}-${mm}-${dd}` };
  }
  if (preset === 'anno') {
    return { data_inizio: `${yyyy}-01-01`, data_fine: `${yyyy}-${mm}-${dd}` };
  }
  if (preset === 'anno_scorso') {
    const prev = yyyy - 1;
    return { data_inizio: `${prev}-01-01`, data_fine: `${prev}-12-31` };
  }
  return customRange;
};

const fmtCurrency = (v) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);

const DashboardGruppoPage = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();

  const [preset, setPreset] = useState('mese');
  const [singleDay, setSingleDay] = useState(new Date());
  const [customFrom, setCustomFrom] = useState(new Date());
  const [customTo, setCustomTo] = useState(new Date());

  const [locali, setLocali] = useState([]);
  const [selectedVenueIds, setSelectedVenueIds] = useState([]); // [] = tutti
  const [includeInattivi, setIncludeInattivi] = useState(false);

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin()) {
      navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const range = useMemo(() => {
    const customRange = { data_inizio: toYmd(customFrom), data_fine: toYmd(customTo) };
    return computeRange(preset, singleDay, customRange);
  }, [preset, singleDay, customFrom, customTo]);

  const loadLocali = useCallback(async () => {
    try {
      const data = await localiApi.getAll(true);
      setLocali(data);
    } catch (e) {
      toast.error('Errore nel caricamento locali');
    }
  }, []);

  const loadStats = useCallback(async () => {
    if (!range?.data_inizio || !range?.data_fine) return;
    setLoading(true);
    try {
      const data = await groupDashboardApi.getStats({
        dataInizio: range.data_inizio,
        dataFine: range.data_fine,
        venueIds: selectedVenueIds,
        includeInattivi,
      });
      setStats(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel caricamento dati di gruppo');
    } finally {
      setLoading(false);
    }
  }, [range, selectedVenueIds, includeInattivi]);

  useEffect(() => {
    loadLocali();
  }, [loadLocali]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const availableLocali = useMemo(() => {
    if (includeInattivi) return locali;
    return locali.filter((l) => l.stato === 'attivo');
  }, [locali, includeInattivi]);

  const toggleVenue = (vid) => {
    setSelectedVenueIds((prev) =>
      prev.includes(vid) ? prev.filter((x) => x !== vid) : [...prev, vid]
    );
  };

  const handleDownloadXlsx = async () => {
    setDownloading(true);
    try {
      const resp = await groupDashboardApi.downloadXlsx({
        dataInizio: range.data_inizio,
        dataFine: range.data_fine,
        venueIds: selectedVenueIds,
        includeInattivi,
      });
      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dashboard_gruppo_${range.data_inizio}_${range.data_fine}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Export scaricato");
    } catch (e) {
      toast.error("Errore durante l'export");
    } finally {
      setDownloading(false);
    }
  };

  // Bar chart: massimo per scalare le barre
  const maxFatturato = useMemo(() => {
    const arr = stats?.fatturato_per_locale || [];
    return arr.reduce((m, r) => Math.max(m, r.totale_fatturato || 0), 0) || 1;
  }, [stats]);

  const isSingleDay = preset === 'oggi' || preset === 'giorno';
  const periodoLabel = isSingleDay
    ? `Giorno: ${range.data_inizio}`
    : `Dal ${range.data_inizio} al ${range.data_fine}`;

  return (
    <div className="min-h-screen pb-24" data-testid="group-dashboard-page">
      <header className="glass-header sticky top-0 z-40 px-4 py-4 border-b border-border">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate('/impostazioni')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Vista trasversale</p>
              <h1 className="text-xl font-bold font-heading tracking-tight">Dashboard di Gruppo</h1>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadXlsx}
            disabled={downloading || !stats}
            data-testid="export-xlsx-btn"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Esporta XLSX
          </Button>
        </div>
      </header>

      <main className="px-4 py-6 max-w-4xl mx-auto space-y-6">
        {/* Filtri */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="text-xs flex items-center gap-1">
                  <CalendarDays className="w-3.5 h-3.5" /> Periodo
                </Label>
                <Select value={preset} onValueChange={setPreset}>
                  <SelectTrigger data-testid="preset-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRESETS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {preset === 'giorno' && (
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-xs">Seleziona il giorno</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="single-day-picker-trigger"
                      >
                        <CalendarDays className="w-4 h-4 mr-2 text-primary" />
                        {singleDay ? format(singleDay, "EEEE d MMMM yyyy", { locale: it }) : 'Scegli una data'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start" data-testid="single-day-picker-popover">
                      <Calendar
                        mode="single"
                        selected={singleDay}
                        onSelect={(d) => d && setSingleDay(d)}
                        initialFocus
                        weekStartsOn={1}
                        locale={it}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {preset === 'custom' && (
                <>
                  <div className="space-y-2">
                    <Label className="text-xs">Dal</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="custom-from-trigger"
                        >
                          <CalendarDays className="w-4 h-4 mr-2 text-primary" />
                          {customFrom ? format(customFrom, "d MMM yyyy", { locale: it }) : 'Dal'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={customFrom}
                          onSelect={(d) => d && setCustomFrom(d)}
                          initialFocus
                          weekStartsOn={1}
                          locale={it}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Al</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="custom-to-trigger"
                        >
                          <CalendarDays className="w-4 h-4 mr-2 text-primary" />
                          {customTo ? format(customTo, "d MMM yyyy", { locale: it }) : 'Al'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={customTo}
                          onSelect={(d) => d && setCustomTo(d)}
                          initialFocus
                          weekStartsOn={1}
                          locale={it}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </>
              )}

              <div className={cn('space-y-2', preset === 'custom' || preset === 'giorno' ? 'sm:col-span-3' : 'sm:col-span-1')}>
                <Label className="text-xs flex items-center gap-1">
                  <Layers className="w-3.5 h-3.5" /> Locali inattivi
                </Label>
                <div className="flex items-center gap-2 h-9">
                  <Switch
                    checked={includeInattivi}
                    onCheckedChange={setIncludeInattivi}
                    data-testid="toggle-include-inattivi"
                  />
                  <span className="text-sm text-muted-foreground">
                    {includeInattivi ? 'Inclusi' : 'Esclusi'}
                  </span>
                </div>
              </div>
            </div>

            <div className="text-xs text-muted-foreground border-t pt-2">
              <CalendarDays className="w-3 h-3 inline mr-1" />
              <span data-testid="periodo-label">{periodoLabel}</span>
            </div>

            {/* Multi-select locali */}
            <div className="space-y-2">
              <Label className="text-xs flex items-center gap-1">
                <Building2 className="w-3.5 h-3.5" /> Locali inclusi
                <span className="text-muted-foreground ml-1">
                  ({selectedVenueIds.length === 0 ? 'tutti' : `${selectedVenueIds.length} selezionati`})
                </span>
              </Label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedVenueIds([])}
                  data-testid="venue-filter-all"
                  className={cn(
                    'px-3 py-1.5 rounded-full border text-xs transition-colors',
                    selectedVenueIds.length === 0
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background border-border hover:bg-muted'
                  )}
                >
                  Tutti
                </button>
                {availableLocali.map((l) => {
                  const selected = selectedVenueIds.includes(l.id);
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => toggleVenue(l.id)}
                      data-testid={`venue-filter-${l.slug}`}
                      className={cn(
                        'px-3 py-1.5 rounded-full border text-xs transition-colors',
                        selected
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-border hover:bg-muted',
                        l.stato !== 'attivo' && 'italic opacity-75'
                      )}
                    >
                      {l.nome} {l.stato !== 'attivo' && '(inattivo)'}
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : stats ? (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="group-kpi-cards">
              <KpiCard icon={Receipt} label="Incasso totale" value={fmtCurrency(stats.kpi.incasso_totale)} testid="kpi-incasso" />
              <KpiCard icon={TrendingUp} label="Totale fatturato" value={fmtCurrency(stats.kpi.totale_fatturato)} accent testid="kpi-fatturato" />
              <KpiCard icon={PiggyBank} label="Totale proforma" value={fmtCurrency(stats.kpi.totale_proforma)} testid="kpi-proforma" />
              <KpiCard icon={Wallet} label="Uscite totali" value={fmtCurrency(stats.kpi.uscite_totali)} testid="kpi-uscite" />
            </div>

            <Card className={cn('border-2', stats.kpi.netto_gruppo >= 0 ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50')}>
              <CardContent className="p-4 flex items-center justify-between" data-testid="netto-gruppo-card">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Netto di gruppo</p>
                  <p className={cn('text-2xl font-bold font-heading', stats.kpi.netto_gruppo >= 0 ? 'text-emerald-700' : 'text-red-700')}>
                    {fmtCurrency(stats.kpi.netto_gruppo)}
                  </p>
                  <p className="text-xs text-muted-foreground">Fatturato − Uscite</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Media giornaliera di gruppo</p>
                  <p className="text-lg font-semibold" data-testid="media-giornaliera-gruppo">
                    {fmtCurrency(stats.kpi.media_giornaliera_gruppo)}
                  </p>
                  <p className="text-[10px] text-muted-foreground italic">
                    = Fatturato gruppo / {stats.kpi.giorni_periodo} giorni di calendario
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Confronto fatturato per locale */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-primary" />
                  <h2 className="font-heading font-semibold">Confronto fatturato per locale</h2>
                </div>
                {stats.fatturato_per_locale.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">Nessun dato nel periodo</p>
                ) : (
                  <div className="space-y-2" data-testid="fatturato-chart">
                    {stats.fatturato_per_locale.map((r) => (
                      <div key={r.venue_id} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium">{r.venue_nome}</span>
                          <span className="font-mono">{fmtCurrency(r.totale_fatturato)}</span>
                        </div>
                        <div className="h-3 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all"
                            style={{ width: `${(r.totale_fatturato / maxFatturato) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tabella dettaglio per locale */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-primary" />
                  <h2 className="font-heading font-semibold">Dettaglio per locale</h2>
                </div>
                {stats.per_locale.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">Nessun locale corrispondente ai filtri</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="per-locale-table">
                      <thead>
                        <tr className="text-left border-b">
                          <th className="py-2 pr-2 font-semibold">Locale</th>
                          <th className="py-2 px-2 text-right font-semibold">Incasso</th>
                          <th className="py-2 px-2 text-right font-semibold">Fatturato</th>
                          <th className="py-2 px-2 text-right font-semibold">Proforma</th>
                          <th className="py-2 px-2 text-right font-semibold">Uscite</th>
                          <th className="py-2 px-2 text-right font-semibold">Coperti</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.per_locale.map((r) => (
                          <tr key={r.venue_id} className="border-b last:border-b-0" data-testid={`row-${r.venue_id}`}>
                            <td className="py-2 pr-2">
                              <div className="font-medium">{r.venue_nome}</div>
                              <div className="text-[11px] text-muted-foreground capitalize">{r.venue_tipo}{r.venue_stato !== 'attivo' && <Badge variant="secondary" className="ml-1 text-[9px]">Inattivo</Badge>}</div>
                            </td>
                            <td className="py-2 px-2 text-right font-mono">{fmtCurrency(r.incasso)}</td>
                            <td className="py-2 px-2 text-right font-mono">{fmtCurrency(r.totale_fatturato)}</td>
                            <td className="py-2 px-2 text-right font-mono">{fmtCurrency(r.totale_proforma)}</td>
                            <td className="py-2 px-2 text-right font-mono">{fmtCurrency(r.uscite)}</td>
                            <td className="py-2 px-2 text-right font-mono">{r.coperti}</td>
                          </tr>
                        ))}
                        <tr className="bg-muted font-semibold">
                          <td className="py-2 pr-2">TOTALE GRUPPO</td>
                          <td className="py-2 px-2 text-right font-mono">{fmtCurrency(stats.kpi.incasso_totale)}</td>
                          <td className="py-2 px-2 text-right font-mono">{fmtCurrency(stats.kpi.totale_fatturato)}</td>
                          <td className="py-2 px-2 text-right font-mono">{fmtCurrency(stats.kpi.totale_proforma)}</td>
                          <td className="py-2 px-2 text-right font-mono">{fmtCurrency(stats.kpi.uscite_totali)}</td>
                          <td className="py-2 px-2 text-right font-mono">{stats.per_locale.reduce((s, r) => s + r.coperti, 0)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            <p className="text-xs text-muted-foreground text-center">
              <FileSpreadsheet className="w-3 h-3 inline mr-1" />
              I dati di questa vista sono in <strong>sola lettura</strong> e sommati a runtime. La dashboard del singolo locale resta indipendente.
            </p>
          </>
        ) : null}
      </main>
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, accent = false, testid }) => (
  <Card className={cn(accent && 'border-primary')} data-testid={testid}>
    <CardContent className="p-3 space-y-1">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <p className={cn('text-lg font-bold font-heading', accent && 'text-primary')}>{value}</p>
    </CardContent>
  </Card>
);

export default DashboardGruppoPage;
